import React, { useState, useRef } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface CitationLinkProps {
    href: string;
    children: React.ReactNode;
    citationLinks?: Record<string, { urls: string[]; source: string; title: string; summary?: string }>;
}

export const CitationLink: React.FC<CitationLinkProps> = ({ href, children, citationLinks }) => {
    const [showPopup, setShowPopup] = useState(false);
    const linkRef = useRef<HTMLAnchorElement>(null);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        
        // Check if this is a citation link (contains "block-")
        const blockId = extractBlockId(href);
        
        if (blockId && citationLinks && citationLinks[blockId]) {
            // Show popup with citation links
            setShowPopup(true);
        } else {
            // Regular link - open in new tab
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    };

    const handleClose = () => {
        setShowPopup(false);
    };

    // Extract block-XX from href (could be just "block-AA" or a full URL with anchor)
    const extractBlockId = (href: string): string | null => {
        // Check if href contains "block-"
        const match = href.match(/block-[A-Z]+/);
        return match ? match[0] : null;
    };

    const blockId = extractBlockId(href);
    const citationData = blockId && citationLinks ? citationLinks[blockId] : null;

    return (
        <>
            <a 
                ref={linkRef}
                href={href} 
                onClick={handleClick}
                style={{ 
                    cursor: 'pointer',
                    color: '#0078d4',
                    textDecoration: 'underline'
                }}
            >
                {children}
            </a>
            
            {showPopup && citationData && (
                <Dialog
                    title="Citation Links"
                    onClose={handleClose}
                    height={'80%'}
                    width={'40%'}
                >
                    <div style={{ padding: '20px' }}>
                        {citationData.urls.length > 0 ? (
                            <div>
                                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                                    Source: {citationData.source}
                                </p>
                                <ul style={{ padding: 0 }}>
                                    {citationData.urls.map((url, index) => (
                                        <li key={index} style={{ marginBottom: '16px' }}>
                                            <a 
                                                href={url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ color: '#0078d4', textDecoration: 'underline' }}
                                            >
                                                {url}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div>
                                <div style={{ marginBottom: '12px' }}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {citationData.title}
                                    </ReactMarkdown>
                                </div>
                                {citationData.summary && (
                                    <div style={{ color: '#666' }}>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {citationData.summary}
                                        </ReactMarkdown>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Dialog>
            )}
        </>
    );
};
